import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Plus, AlertTriangle } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "../components/Navigation";
import Card from "../components/Card";
import Button from "../components/Buttons";
import LoadingSpinner from "../components/LoadingSpinner";
import TPRMNewVendorModal from "../components/TPRMNewVendorModal";

const getRiskBarColor = (level) => {
  switch (level) {
    case "CRITICAL": return "bg-red-700";
    case "HIGH": return "bg-orange-500";
    case "MEDIUM": return "bg-yellow-500";
    case "LOW": return "bg-green-500";
    default: return "bg-gray-400";
  }
};

const getAviColor = (val) => {
  if (val == null) return "text-gray-400";
  if (val > 80) return "text-red-700";
  if (val > 60) return "text-orange-600";
  if (val > 30) return "text-yellow-600";
  return "text-green-600";
};

const getRpiColor = (val) => {
  if (val == null) return "text-gray-400";
  const pct = val * 100;
  if (pct > 80) return "text-red-700";
  if (pct > 60) return "text-orange-600";
  if (pct > 30) return "text-yellow-600";
  return "text-green-600";
};

const formatImpact = (val) => {
  if (val == null) return "\u2014";
  const n = Number(val);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  return `$${Math.round(n).toLocaleString()}`;
};

const getTierLabel = (tier) => {
  switch (tier) {
    case 1: return { text: "Tier 1", color: "bg-red-100 text-red-800" };
    case 2: return { text: "Tier 2", color: "bg-yellow-100 text-yellow-800" };
    case 3: return { text: "Tier 3", color: "bg-green-100 text-green-800" };
    default: return null;
  }
};

const TPRMVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);
  const [tierFilter, setTierFilter] = useState("");

  const [reassessmentGaps, setReassessmentGaps] = useState([]);

  const apiCall = useApi();
  const navigate = useNavigate();
  const { canCreateVendor, canManageOrganizationRisks } = useRBAC();

  const canAdd = canCreateVendor();
  const canManageRisks = canManageOrganizationRisks();

  const fetchVendorRiskSummary = async () => {
    try {
      setLoading(true);
      const data = await apiCall("GET", "/TPRMVendor/risk-summary");
      setVendors(Array.isArray(data) ? data : []);
    } catch (err) {
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  // UN EOI Phase 4: assessed vendors with no tier and no cadence override are excluded from
  // auto-reassessment — surface them so a risk manager assigns a tier instead of silently skipping.
  const fetchReassessmentGaps = async () => {
    try {
      const data = await apiCall("GET", "/TPRMVendor/reassessment-gaps");
      setReassessmentGaps(data?.vendors || []);
    } catch (err) {
      setReassessmentGaps([]);
    }
  };

  useEffect(() => {
    fetchVendorRiskSummary();
    if (canManageRisks) fetchReassessmentGaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-content-primary">TPRM Vendors</h1>
          <p className="text-sm text-content-secondary mt-1">Manage and monitor third-party vendor risks</p>
        </div>

        {reassessmentGaps.length > 0 && (
          <div className="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg border border-amber-300 bg-amber-50">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold">
                {reassessmentGaps.length} assessed vendor{reassessmentGaps.length === 1 ? "" : "s"} excluded from automated reassessment
              </p>
              <p className="mt-0.5">
                These vendors have no tier and no reassessment frequency override, so no reassessment
                will be scheduled automatically. Assign a tier to include them:{" "}
                {reassessmentGaps.map((v, i) => (
                  <React.Fragment key={v.vendorId}>
                    {i > 0 && ", "}
                    <button
                      type="button"
                      className="underline hover:text-amber-950"
                      onClick={() => navigate(`/VendorDashboard/${v.vendorId}`)}
                    >
                      {v.vendorName}
                    </button>
                  </React.Fragment>
                ))}
              </p>
            </div>
          </div>
        )}

        <Card>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-content-primary">Vendors</h3>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-700"
              >
                <option value="">All Tiers</option>
                <option value="1">Tier 1 - Critical</option>
                <option value="2">Tier 2 - Important</option>
                <option value="3">Tier 3 - Standard</option>
                <option value="none">Unclassified</option>
              </select>
            </div>
            {canAdd && (
              <Button size="sm" onClick={() => setShowNewVendorModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Vendor
              </Button>
            )}
          </div>

          {vendors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No vendors found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {vendors.filter(v => {
                if (!tierFilter) return true;
                if (tierFilter === "none") return !v.vendorTier;
                return v.vendorTier === Number(tierFilter);
              }).map((vendor) => (
                <div
                  key={vendor.vendorId}
                  className="p-4 border-2 rounded-lg cursor-pointer transition-colors border-gray-200 hover:border-gray-300"
                  onClick={() => navigate(`/VendorDashboard/${vendor.vendorId}`)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-content-primary">{vendor.vendorName}</h3>
                      {vendor.vendorDomain && (
                        <p className="text-sm text-gray-600">{vendor.vendorDomain}</p>
                      )}
                    </div>
                    <div className="ml-4 flex items-center gap-2">
                      {getTierLabel(vendor.vendorTier) && (
                        <span className={`px-2 py-1 text-xs rounded font-medium ${getTierLabel(vendor.vendorTier).color}`}>
                          {getTierLabel(vendor.vendorTier).text}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {vendor.totalRisks || 0} Active Risks
                      </span>
                    </div>
                  </div>

                  {/* Risk Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">Risk Level</span>
                      <span className="text-xs font-medium text-content-primary">
                        {vendor.riskLevel || 'No Risk'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getRiskBarColor(vendor.riskLevel)}`}
                        style={{ width: `${vendor.riskScore || 0}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Risk Count Badges */}
                  {(vendor.criticalCount > 0 || vendor.highCount > 0 || vendor.mediumCount > 0 || vendor.lowCount > 0) && (
                    <div className="flex items-center mt-2 space-x-2">
                      {vendor.criticalCount > 0 && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                          {vendor.criticalCount} Critical
                        </span>
                      )}
                      {vendor.highCount > 0 && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                          {vendor.highCount} High
                        </span>
                      )}
                      {vendor.mediumCount > 0 && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                          {vendor.mediumCount} Medium
                        </span>
                      )}
                      {vendor.lowCount > 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                          {vendor.lowCount} Low
                        </span>
                      )}
                    </div>
                  )}

                  {/* AVI / RPI Metrics */}
                  {(vendor.axarisVendorImpactIndex != null || vendor.ransomwarePredictionIndex != null) && (
                    <div className="flex items-center mt-2 space-x-4 text-sm">
                      {vendor.axarisVendorImpactIndex != null && (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500 text-xs font-medium">AVI:</span>
                          <span className={`font-semibold ${getAviColor(vendor.axarisVendorImpactIndex)}`}>
                            {formatImpact(vendor.axarisVendorImpactIndex)}{vendor.axarisVendorImpactIndex != null ? '+' : ''}
                          </span>
                        </div>
                      )}
                      {vendor.ransomwarePredictionIndex != null && (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500 text-xs font-medium">RPI:</span>
                          <span className={`font-semibold ${getRpiColor(vendor.ransomwarePredictionIndex)}`}>
                            {(vendor.ransomwarePredictionIndex * 100).toFixed(1)}%
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {showNewVendorModal && (
        <TPRMNewVendorModal
          isOpen={showNewVendorModal}
          onClose={() => setShowNewVendorModal(false)}
          onVendorCreated={() => {
            setShowNewVendorModal(false);
            fetchVendorRiskSummary();
          }}
        />
      )}
    </div>
  );
};

export default TPRMVendors;
